import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiRequest, UNAUTHORIZED_EVENT } from '@/lib/apiClient'
import { clearStoredSession, getStoredToken, setStoredSession } from '@/lib/tokenStore'
import type {
  GoogleSignInResponse,
  PublicUserWithAccess,
  SessionInfo,
  SignInResponse,
  TwoFactorEmailResponse,
  TwoFactorMethod,
  UserEntity,
} from '@/types/api'
import {
  AuthContext,
  type AuthContextValue,
  type PendingSignIn,
  type SignInOutcome,
} from './auth-context'

const ME_QUERY_KEY = ['auth', 'me'] as const

const fetchMe = () => apiRequest<PublicUserWithAccess>('/auth/me')

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [hasSession, setHasSession] = useState(() => Boolean(getStoredToken()))

  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    enabled: hasSession,
    retry: false,
    staleTime: 60_000,
  })

  // When the API rejects the stored session (expired/revoked), sign out here.
  useEffect(() => {
    const handleUnauthorized = () => {
      setHasSession(false)
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY })
      navigate('/signin', { replace: true })
    }
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
  }, [navigate, queryClient])

  const adoptSession = useCallback(
    async (session: SessionInfo) => {
      setStoredSession({ token: session.token, expiresAt: session.expiresAt })
      setHasSession(true)
      // Load permissions/groups before the app renders its navigation, and
      // return the profile so callers can route by onboarding step.
      return queryClient.fetchQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
    },
    [queryClient],
  )

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInOutcome> => {
      const result = await apiRequest<SignInResponse>('/auth/signin', {
        method: 'POST',
        body: { email, password },
      })
      if (result.twoFactorRequired) return { twoFactorRequired: true }
      if (!result.session) throw new Error('Sign-in did not return a session.')
      return { twoFactorRequired: false, user: await adoptSession(result.session) }
    },
    [adoptSession],
  )

  const signInWithGoogle = useCallback(
    async (credential: string): Promise<SignInOutcome> => {
      const result = await apiRequest<GoogleSignInResponse>('/auth/google/token', {
        method: 'POST',
        body: { credential },
      })
      if (result.twoFactorRequired) return { twoFactorRequired: true }
      if (!result.session) throw new Error('Google sign-in did not return a session.')
      return { twoFactorRequired: false, user: await adoptSession(result.session) }
    },
    [adoptSession],
  )

  /** Body for a sign-in request that may carry a second-factor code. */
  const credentialBody = (credentials: PendingSignIn) =>
    'credential' in credentials ? { credential: credentials.credential } : credentials

  const completeTwoFactor = useCallback(
    async ({
      credentials,
      code,
      method,
    }: {
      credentials: PendingSignIn
      code: string
      method: TwoFactorMethod
    }) => {
      const isGoogle = 'credential' in credentials
      const result = await apiRequest<SignInResponse | GoogleSignInResponse>(
        isGoogle ? '/auth/google/token' : '/auth/signin',
        { method: 'POST', body: { ...credentialBody(credentials), code, method } },
      )
      if (!result.session) throw new Error('Two-factor verification did not return a session.')
      return adoptSession(result.session)
    },
    [adoptSession],
  )

  const requestTwoFactorEmail = useCallback(
    (credentials: PendingSignIn) =>
      apiRequest<TwoFactorEmailResponse>('/auth/2fa/email', {
        method: 'POST',
        body: credentialBody(credentials),
      }),
    [],
  )

  const signOut = useCallback(async (options?: { notice?: string }) => {
    try {
      await apiRequest('/auth/signout', { method: 'POST' })
    } catch {
      // Best-effort: the session may already be invalid server-side.
    }
    clearStoredSession()
    setHasSession(false)
    queryClient.removeQueries({ queryKey: ME_QUERY_KEY })
    navigate('/signin', { replace: true, state: options?.notice ? { notice: options.notice } : null })
  }, [navigate, queryClient])

  const reload = useCallback(async () => {
    await meQuery.refetch()
  }, [meQuery])

  const value = useMemo<AuthContextValue>(() => {
    const permissions = meQuery.data?.permissions ?? []
    return {
      user: meQuery.data ?? null,
      permissions,
      groups: meQuery.data?.groups ?? [],
      isAuthenticated: Boolean(meQuery.data),
      isLoading: hasSession && meQuery.isLoading,
      isError: hasSession && meQuery.isError,
      hasPermission: (code: string) => permissions.includes(code),
      hasAnyPermission: (codes: readonly string[]) => codes.some((code) => permissions.includes(code)),
      hasEntity: (entity: UserEntity | UserEntity[]) =>
        Array.isArray(entity)
          ? entity.includes(meQuery.data?.entity as UserEntity)
          : meQuery.data?.entity === entity,
      signIn,
      signInWithGoogle,
      completeTwoFactor,
      requestTwoFactorEmail,
      adoptSession,
      signOut,
      reload,
    }
  }, [
    meQuery.data,
    meQuery.isLoading,
    meQuery.isError,
    hasSession,
    signIn,
    signInWithGoogle,
    completeTwoFactor,
    requestTwoFactorEmail,
    adoptSession,
    signOut,
    reload,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
